import React from 'react';
import { useNavigate } from 'react-router-dom';
import pb from '../pocketbase';
import { isPWAInstalled } from '../utils/pwaUtils';
import { usePageCache } from '../hooks/usePageCache';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator/PullToRefreshIndicator';
import './ProfilePage.css';

export default function ProfilePage({ onResetAppState, onPWAInstall, onPWASkip }) {
  const navigate = useNavigate();
  const [state, setState] = React.useState({
    loading: true,
    displayName: '',
    at: '',
    avatar: '',
    hasSubscription: false,
    isTrial: false,
    subscriptionEndsAt: null,
    subscription: null,
    error: null,
  });

  const [isPWA, setIsPWA] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const { getCachedData } = usePageCache();

  // Используем кэш для первой загрузки
  const cachedData = getCachedData('profile', async () => {
    const user = pb.authStore.model;
    if (!user) {
      window.location.assign('/login');
      return;
    }

    // Для PocketBase берём данные из authStore.model (users collection)
    // Подписки пока не реализованы в PocketBase версии
    const subscription = null;

    const meta = user || {};
    const displayName = meta.full_name?.trim() ||
      meta.username ||
      'Без имени';
    const at = meta.username ? '@' + meta.username : '@user';
    const avatar = meta.avatar || '';
    const hasSubscription = !!(subscription && subscription.status && (subscription.status === 'active' || subscription.status === 'trialing'));
    const isTrial = subscription?.status === 'trialing';
    const subscriptionEndsAt = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;

    return {
      displayName,
      at,
      avatar,
      hasSubscription,
      isTrial,
      subscriptionEndsAt,
      subscription
    };
  }, []);

  // Обновляем состояние из кэша при первой загрузке
  React.useEffect(() => {
    if (cachedData.data && isInitialLoad) {
      setState(prev => ({
        ...prev,
        loading: false,
        ...cachedData.data,
        error: null
      }));
      setIsPWA(isPWAInstalled());
      setIsInitialLoad(false);
    }
  }, [cachedData.data, isInitialLoad]);

  // Обновляем состояние загрузки из кэша
  React.useEffect(() => {
    if (cachedData.loading !== undefined) {
      setState(prev => ({ ...prev, loading: cachedData.loading }));
    }
  }, [cachedData.loading]);

  // Функция для pull-to-refresh
  const handleRefresh = React.useCallback(async () => {
    await cachedData.refresh();
  }, [cachedData]);

  // Настройка pull-to-refresh
  const {
    elementRef,
    isRefreshing,
    isPulling,
    pullDistance,
    refreshProgress,
    canRefresh
  } = usePullToRefresh(handleRefresh, {
    threshold: 80,
    resistance: 2.5,
    maxPullDistance: 120,
    enabled: !state.loading && !state.error
  });

  async function onSignOut() {
    try {
      pb.authStore.clear();
    } finally {
      window.location.assign('/');
    }
  }

  if (state.loading) return (
    <main className="page">
      <h1>Профиль</h1>
      <div className="skeleton-card" />
    </main>
  );

  if (state.error) return (
    <main className="page">
      <h1>Профиль</h1>
      <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
        <h3>Ошибка загрузки профиля</h3>
        <p>{state.error}</p>
        <button
          onClick={() => cachedData.refresh()}
          style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
        >
          Попробовать снова
        </button>
      </div>
    </main>
  );

  return (
    <>
      <PullToRefreshIndicator
        isRefreshing={isRefreshing}
        isPulling={isPulling}
        progress={refreshProgress}
        canRefresh={canRefresh}
        pullDistance={pullDistance}
      />
      <main className="page" ref={elementRef}>
        <h1>Профиль</h1>

        <div className={`profile-card ${state.hasSubscription ? 'profile-card--subscription' : ''}`}>
          {state.avatar ? (
            <img src={state.avatar} alt="Аватар" className="avatar" />
          ) : (
            <div className="avatar" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#666'
            }}>
              {state.displayName.charAt(0).toUpperCase()}
            </div>
          )}

          <div className="name-block">
            <div className="display-name">{state.displayName}</div>
            <div className="username">{state.at}</div>

            {state.hasSubscription && (
              <div className="subscription-badge">
                {`${state.subscription?.subscription_plans?.name || ''}${state.subscriptionEndsAt ? ` до ${state.subscriptionEndsAt.toLocaleDateString('ru-RU')}` : ''}`}
              </div>
            )}
          </div>
        </div>

        {/* {!state.hasSubscription && (
          <div className="subscription-cta">
            <div className="subscription-cta-content">
              <h3 className="subscription-cta-title">Получите доступ ко всем играм</h3>
              <p className="subscription-cta-description">
                Подпишитесь и получите неограниченный доступ к библиотеке игр, 
                эксклюзивному контенту и приоритетной поддержке.
              </p>
              <button className="subscription-cta-button">
                Оформить подписку
              </button>
            </div>
          </div>
        )} */}

        <div className="menu">
          {!isPWA && (
            <>
              <button className="menu-item" onClick={onPWAInstall}>
                <div className="menu-item-content">
                  <div className="menu-icon">
                    <img src="/icons/install-app-profile.svg" alt="Установить" width="24" height="24" />
                  </div>
                  <span className="menu-label">Установить приложение</span>
                </div>
                <div className="chevron">
                  <img src="/icons/forward-profile.svg" alt=">" width="16" height="16" />
                </div>
              </button>
            </>
          )}
          {/* 
          <button className="menu-item" onClick={() => navigate('/my-notes')}>
            <div className="menu-item-content">
              <div className="menu-icon">
                <img src="/icons/note-profile.svg" alt="Заметки" width="24" height="24" />
              </div>
              <span className="menu-label">Мои заметки</span>
            </div>
            <div className="chevron">
              <img src="/icons/forward-profile.svg" alt=">" width="16" height="16" />
            </div>
          </button> */}


          {/* <button className="menu-item" onClick={() => {}}>
            <div className="menu-item-content">
              <div className="menu-icon">
                <img src="/icons/payment-profile.svg" alt="Оплата" width="24" height="24" />
              </div>
              <span className="menu-label">Оплата</span>
            </div>
            <div className="chevron">
              <img src="/icons/forward-profile.svg" alt=">" width="16" height="16" />
            </div>
          </button> */}
        </div>


        <button className="menu-item" onClick={() => navigate('/legal-info')}>
          <div className="menu-item-content">
            <div className="menu-icon">
              <img src="/icons/legal-info-profile.svg" alt="Правовая информация" width="24" height="24" />
            </div>
            <span className="menu-label">Правовая информация</span>
          </div>
          <div className="chevron">
            <img src="/icons/forward-profile.svg" alt=">" width="16" height="16" />
          </div>
        </button>


        <button className="menu-item" onClick={() => window.open('https://t.me/SenseApp_form', '_blank')}>
          <div className="menu-item-content">
            <div className="menu-icon">
              <img src="/icons/help-profile.svg" alt="Помощь" width="24" height="24" />
            </div>
            <span className="menu-label">Помощь</span>
          </div>
          <div className="chevron">
            <img src="/icons/forward-profile.svg" alt=">" width="16" height="16" />
          </div>
        </button>


        <button className="signout" onClick={onSignOut}>
          <div className="signout-ico">
            <img src="/icons/exit-profile.svg" alt="Выйти" width="20" height="20" />
          </div>
          Выйти
        </button>

      </main>
    </>
  );
}